"use client";
import type { Media } from "@mw/shared/payload-types";

import { useActivePlayer } from "@/app/_providers/active-player";
import { getAudioUrl } from "@/app/_utilities/payload";
import { Badge } from "@components/core/Badge";
import { Button } from "@components/core/Button";
import { Image } from "@components/core/Image";
import Pause20 from "@ui/icons/20x20/pause-20.svg";
import Play20 from "@ui/icons/20x20/play-20.svg";
import { cn } from "@ui/lib/utils";
import * as m from "motion/react-m";
import { useEffect, useId, useRef, useState } from "react";

type tags = {
  className: string;
  name: string;
};

export interface PlayerButtonProps {
  buttonSize?: "md" | "sm";
  buttonVariant?: "primary" | "secondary";
  onClick: () => void;
  playingState: "paused" | "playing";
}

export const PlayerButton = ({
  playingState,
  onClick,
  buttonSize = "md",
  buttonVariant = "secondary",
}: PlayerButtonProps) => {
  return (
    <Button
      className={cn("me-10 flex items-center md:me-0", {
        "h-32 w-32": buttonSize === "sm",
        "h-44 w-44 md:h-48 md:w-48": buttonSize === "md",
      })}
      onClick={onClick}
      variant={buttonVariant}
    >
      <m.span animate={{ opacity: 1, scale: 1 }} initial={{ opacity: 0, scale: 0.8 }} key={playingState}>
        {playingState === "playing" ? <Pause20 className='color-white' /> : <Play20 className='color-white' />}
      </m.span>
    </Button>
  );
};

export const Player = ({
  classNames,
  name,
  audioSrc,
  tags,
  imgSrc,
  playerButton,
  onPlay,
  onPause,
}: {
  audioSrc: Media | string;
  classNames?: string;
  imgSrc: Media | string;
  inCard?: boolean;
  name: string;
  onPause?: () => void;
  onPlay?: () => void;
  playerButton?: (props: PlayerButtonProps) => React.ReactNode;
  tags?: tags[];
}) => {
  const { activePlayer, setActivePlayer } = useActivePlayer();
  // no loading state so it doesn't show lots of spinners
  const [playingState, setPlayingState] = useState<"paused" | "playing">("paused");
  const audioElementRef = useRef<HTMLAudioElement>(null);

  const randomId = useId();

  // only have one player at a time
  useEffect(() => {
    if (activePlayer != randomId) {
      audioElementRef.current?.pause();
      setPlayingState("paused");
    }
  }, [activePlayer, randomId]);

  const handleButtonClick = () => {
    if (playingState == "paused") {
      setActivePlayer(randomId);
      audioElementRef.current?.play();
      onPlay?.();
    } else {
      audioElementRef.current?.pause();
      onPause?.();
    }
    setPlayingState(prev => (prev === "playing" ? "paused" : "playing"));
  };

  return (
    <>
      <audio
        onEnded={() => setPlayingState("paused")}
        onPause={() => setPlayingState("paused")}
        onPlay={() => setPlayingState("playing")}
        ref={audioElementRef}
        src={typeof audioSrc === "string" ? audioSrc : getAudioUrl(audioSrc)}
      />
      <div className='relative flex h-auto flex-1 flex-row-reverse items-center justify-end md:f-ui-01 md:flex-row md:justify-start md:justify-between'>
        <div className='flex items-center'>
          <div
            className={cn(
              "user-select-none pointer-events-none shrink-0 overflow-hidden rounded-full", // General
              "absolute left-0 top-0 h-24 w-24 -translate-x-1/4 -translate-y-1/4 bg-darker", // Mobile
              "md:static md:me-12 md:h-48 md:w-48 md:translate-x-0 md:translate-y-0 md:bg-dark", // Medium+
            )}
          >
            <Image
              alt={name}
              fit='cover'
              height={40}
              resource={imgSrc}
              style={{ width: "100%", height: "100%" }}
              width={40}
            />
          </div>
          <div className='flex flex-1 flex-col gap-6'>
            <div className='f-ui-03 text-default'>{name}</div>
            <div className='flex gap-4 text-default'>
              {tags?.map((tag, index) => (
                <Badge className={cn("f-ui-02", tag.className)} key={index}>
                  {tag.name}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        {playerButton ? (
          playerButton({
            onClick: handleButtonClick,
            playingState,
          })
        ) : (
          <PlayerButton onClick={handleButtonClick} playingState={playingState} />
        )}
      </div>
    </>
  );
};
